import React, { useState, useEffect } from 'react';
import {
  IconActivity,
  IconConversation,
  IconMessage,
  IconSMS,
  IconVoiceCall,
  IconWarning,
  IconWhatsApp,
} from '../components/icons';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import Button from '../components/Button';
import { Label, Input, Textarea, Select } from '../components/Input';
import Alert from '../components/Alert';
import { testAPI, healthAPI, elevenLabsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Testing = () => {
  const { user } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState('checking');

  // SMS Test State
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsAlert, setSmsAlert] = useState(null);
  const [smsData, setSmsData] = useState({ recipient: '', message: '' });

  // IVR Test State
  const [ivrLoading, setIvrLoading] = useState(false);
  const [ivrAlert, setIvrAlert] = useState(null);
  const [ivrData, setIvrData] = useState({
    phoneNumber: '',
    agentId: '',
  });

  // WhatsApp Test State
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappAlert, setWhatsappAlert] = useState(null);
  const [whatsappData, setWhatsappData] = useState({ destination: '', message: '' });

  const healthTopics = [
    'Malaria',
    'Pre-eclampsia',
    'Diabetes',
    'Hypertension',
    'HIV/AIDS',
    'Tuberculosis',
    'COVID-19',
    'Maternal Health',
    'Child Nutrition',
  ];

  // Check API connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        await healthAPI.check();
        setConnectionStatus('connected');
      } catch (error) {
        setConnectionStatus('error');
        console.error('API connection failed:', error);
      }
    };
    checkConnection();
  }, []);

  // SMS Test Handler
  const handleSMSTest = async (e) => {
    e.preventDefault();
    setSmsAlert(null);
    setSmsLoading(true);

    try {
      const result = await testAPI.sendSMS({
        recipient: smsData.recipient,
        message: smsData.message,
      });

      setSmsAlert({
        type: 'success',
        title: 'SMS Sent Successfully!',
        message: `Message sent to ${smsData.recipient}. ${result.message || ''}`,
      });

      // Reset form
      setSmsData({ recipient: '', message: '' });
    } catch (error) {
      console.error('SMS test error:', error);
      setSmsAlert({
        type: 'error',
        title: 'SMS Test Failed',
        message: error.userMessage || error.response?.data?.detail || error.message,
      });
    } finally {
      setSmsLoading(false);
    }
  };

  // IVR Test Handler - Using ElevenLabs Voice Agent
  const handleIVRTest = async (e) => {
    e.preventDefault();
    setIvrAlert(null);
    setIvrLoading(true);

    try {
      // Format phone number to E.164
      let phone = ivrData.phoneNumber.trim();
      if (phone.startsWith('0')) {
        phone = '+233' + phone.substring(1);
      } else if (!phone.startsWith('+')) {
        phone = '+233' + phone;
      }

      const result = await elevenLabsAPI.makeCall({
        phone_number: phone,
        agent_id: ivrData.agentId.trim(),
        user_id: user?.id ?? null,
      });

      setIvrAlert({
        type: 'success',
        title: 'AI Voice Call Initiated!',
        message: `AI agent is now calling ${phone}! Call ID: ${result.call_id || result.id || 'pending'}`,
      });

      // Reset form
      setIvrData({
        phoneNumber: '',
        agentId: '',
      });
    } catch (error) {
      console.error('IVR test error:', error);
      setIvrAlert({
        type: 'error',
        title: 'AI Voice Test Failed',
        message: error.userMessage || error.response?.data?.detail || error.message,
      });
    } finally {
      setIvrLoading(false);
    }
  };

  // WhatsApp Test Handler
  const handleWhatsAppTest = async (e) => {
    e.preventDefault();
    setWhatsappAlert(null);
    setWhatsappLoading(true);

    try {
      const result = await testAPI.sendWhatsApp({
        destination: whatsappData.destination,
        message: whatsappData.message,
      });

      setWhatsappAlert({
        type: 'success',
        title: 'WhatsApp Message Sent!',
        message: `Message sent to ${whatsappData.destination}. ${result.message || ''}`,
      });

      // Reset form
      setWhatsappData({ destination: '', message: '' });
    } catch (error) {
      console.error('WhatsApp test error:', error);
      setWhatsappAlert({
        type: 'error',
        title: 'WhatsApp Test Failed',
        message: error.userMessage || error.response?.data?.detail || error.message,
      });
    } finally {
      setWhatsappLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <IconActivity className="h-7 w-7 mr-2 text-blue-600" />
          Channel Testing
        </h2>
        <p className="text-gray-600 mt-1">
          Test individual communication channels before launching campaigns
        </p>
      </div>

      {/* Connection Status */}
      {connectionStatus === 'error' && (
        <Alert
          type="error"
          title="Backend Connection Failed"
          message="Cannot connect to the CareArena backend API. Please verify the backend URL is correct."
        />
      )}

      {connectionStatus === 'connected' && (
        <div className="flex items-center space-x-2 text-sm text-green-600">
          <IconActivity className="h-4 w-4" />
          <span>Connected to backend API</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* SMS Test Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <IconMessage className="h-5 w-5 mr-2 text-blue-600" />
              Test SMS
            </CardTitle>
            <CardDescription>Send a test SMS to verify delivery</CardDescription>
          </CardHeader>
          <CardContent>
            {smsAlert && (
              <Alert
                type={smsAlert.type}
                title={smsAlert.title}
                message={smsAlert.message}
                className="mb-4"
              />
            )}

            <form onSubmit={handleSMSTest} className="space-y-4">
              <div>
                <Label htmlFor="sms-recipient">Recipient Phone *</Label>
                <Input
                  id="sms-recipient"
                  type="tel"
                  placeholder="0241234567 or 233241234567"
                  value={smsData.recipient}
                  onChange={(e) => setSmsData({ ...smsData, recipient: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Ghana format: 024, 055, 020, etc.</p>
              </div>

              <div>
                <Label htmlFor="sms-message">
                  Message ({smsData.message.length}/160)
                </Label>
                <Textarea
                  id="sms-message"
                  placeholder="Enter your test message..."
                  rows={4}
                  maxLength={160}
                  value={smsData.message}
                  onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={smsLoading}
              >
                <IconMessage className="h-4 w-4 mr-2" />
                Send Test SMS
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* IVR Test Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <IconVoiceCall className="h-5 w-5 mr-2 text-indigo-600" />
              Test AI Voice Call
            </CardTitle>
            <CardDescription>Make a test AI voice call with an agent</CardDescription>
          </CardHeader>
          <CardContent>
            {ivrAlert && (
              <Alert
                type={ivrAlert.type}
                title={ivrAlert.title}
                message={ivrAlert.message}
                className="mb-4"
              />
            )}

            <form onSubmit={handleIVRTest} className="space-y-4">
              <div>
                <Label htmlFor="ivr-phone">Phone Number *</Label>
                <Input
                  id="ivr-phone"
                  type="tel"
                  placeholder="+233241234567 or 0241234567"
                  value={ivrData.phoneNumber}
                  onChange={(e) => setIvrData({ ...ivrData, phoneNumber: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="ivr-agent">
                  Agent ID
                  <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                </Label>
                <Input
                  id="ivr-agent"
                  placeholder="Leave empty to use default agent"
                  value={ivrData.agentId}
                  onChange={(e) => setIvrData({ ...ivrData, agentId: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use the default configured agent
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-xs text-blue-800 font-medium mb-1">
                  ℹ️ Conversational AI Speech-to-Speech
                </p>
                <p className="text-xs text-blue-700">
                  Uses conversational AI agents for natural, real-time voice interactions.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  <IconWarning className="h-4 w-4 shrink-0" /> This will make an actual phone call and may incur charges.
                </p>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={ivrLoading}
              >
                <IconVoiceCall className="h-4 w-4 mr-2" />
                Make AI Voice Call
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* WhatsApp Test Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <IconConversation className="h-5 w-5 mr-2 text-green-600" />
              Test WhatsApp
            </CardTitle>
            <CardDescription>Send a test WhatsApp message</CardDescription>
          </CardHeader>
          <CardContent>
            {whatsappAlert && (
              <Alert
                type={whatsappAlert.type}
                title={whatsappAlert.title}
                message={whatsappAlert.message}
                className="mb-4"
              />
            )}

            <form onSubmit={handleWhatsAppTest} className="space-y-4">
              <div>
                <Label htmlFor="wa-destination">WhatsApp Number *</Label>
                <Input
                  id="wa-destination"
                  type="tel"
                  placeholder="233241234567"
                  value={whatsappData.destination}
                  onChange={(e) => setWhatsappData({ ...whatsappData, destination: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Format: 233XXXXXXXXX</p>
              </div>

              <div>
                <Label htmlFor="wa-message">Message</Label>
                <Textarea
                  id="wa-message"
                  placeholder="Enter your test message..."
                  rows={5}
                  value={whatsappData.message}
                  onChange={(e) => setWhatsappData({ ...whatsappData, message: e.target.value })}
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={whatsappLoading}
              >
                <IconConversation className="h-4 w-4 mr-2" />
                Send WhatsApp
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card>
        <CardHeader>
          <CardTitle>Testing Guidelines</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <IconSMS className="h-4 w-4 text-gray-400" /> SMS Testing
              </h4>
              <ul className="space-y-1 text-gray-600">
                <li>• Use your own number first</li>
                <li>• Check character limit (160)</li>
                <li>• Verify message delivery</li>
                <li>• Test with Ghana phone formats</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <IconVoiceCall className="h-4 w-4 text-gray-400" /> AI Voice Testing
              </h4>
              <ul className="space-y-1 text-gray-600">
                <li>• Call your own number</li>
                <li>• Verify AI greeting</li>
                <li>• Test bidirectional conversation</li>
                <li>• Check call duration</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <IconWhatsApp className="h-4 w-4 text-gray-400" /> WhatsApp Testing
              </h4>
              <ul className="space-y-1 text-gray-600">
                <li>• Send to your WhatsApp</li>
                <li>• Verify message delivery</li>
                <li>• Test AI chatbot responses</li>
                <li>• Check webhook setup</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Testing;
